import dayjs from "dayjs";
import { TIME_DESCRIPTION_MAX_LENGTH, WEEK_DAYS } from "@shared/helpers/constants";
import { hasValidCoordinates } from "@shared/helpers/geo";

export type GroupInstanceScheduleRangeMode = "until_date" | "weeks";

export type GroupInstanceScheduleConfig = {
  days: (typeof WEEK_DAYS)[number][];
  startFrom: Date;
  startTime: { hour: number; minute: number };
  endTime: { hour: number; minute: number };
  rangeMode: GroupInstanceScheduleRangeMode;
  endDate?: Date;
  weekCount?: number;
};

export type GeneratedGroupInstanceSlot = {
  startDate: Date;
  endDate: Date;
  day: (typeof WEEK_DAYS)[number];
};

export type GroupInstanceSlotPreviewStatus = "new" | "duplicate" | "past";

export type GroupInstanceSlotPreview = GeneratedGroupInstanceSlot & {
  status: GroupInstanceSlotPreviewStatus;
};

export type GroupInstanceSlotIdentity = {
  startDate?: Date | null;
  hasTime?: boolean | null;
  timeDescription?: string | null;
  location?: string | null;
  isRemote?: boolean | null;
};

export type BulkLocationEntry = {
  location: string;
  coordinates: { lat: number; lng: number };
  isRemote: boolean;
};

export type BulkMultiLocationSchedule =
  | {
      hasTime: true;
      startDate: Date;
      endDate: Date;
      timeDescription?: string;
    }
  | {
      hasTime: false;
      startDate?: null;
      endDate?: null;
      timeDescription: string;
    };

export type MultiLocationSlotPreview = BulkLocationEntry & {
  hasTime: boolean;
  hasStartTime: boolean;
  startDate: Date | null;
  endDate: Date | null;
  timeDescription: string;
  day: (typeof WEEK_DAYS)[number];
  status: GroupInstanceSlotPreviewStatus;
};

export const GROUP_INSTANCE_BULK_WARN_THRESHOLD = 100;
export const GROUP_INSTANCE_BULK_MAX = 200;

const REMOTE_LOCATION_LABEL = "מרחוק";

/** Minute-precision key - timezone-safe time component for duplicate detection. */
export function groupInstanceSlotKey(startDate: Date): string {
  const t = new Date(startDate).getTime();
  if (!Number.isFinite(t)) return "";
  return String(Math.floor(t / 60_000));
}

export function groupInstanceLocationKey(
  isRemote?: boolean | null,
  location?: string | null,
): string {
  if (isRemote) return "remote";
  return (location ?? "").trim().toLowerCase();
}

/** Identity for duplicate detection: schedule + location. */
export function groupInstanceIdentityKey(
  slot: GroupInstanceSlotIdentity,
): string {
  const loc = groupInstanceLocationKey(slot.isRemote, slot.location);
  if (slot.hasTime === false) {
    const desc = (slot.timeDescription ?? "").trim().toLowerCase();
    return `desc:${desc}|${loc}`;
  }
  if (!slot.startDate) return `|${loc}`;
  return `${groupInstanceSlotKey(new Date(slot.startDate))}|${loc}`;
}

function dayIndex(day: (typeof WEEK_DAYS)[number]): number {
  return WEEK_DAYS.indexOf(day);
}

function applyTime(
  date: dayjs.Dayjs,
  time: { hour: number; minute: number },
): Date {
  return date
    .hour(time.hour)
    .minute(time.minute)
    .second(0)
    .millisecond(0)
    .toDate();
}

function buildSlot(
  date: dayjs.Dayjs,
  day: (typeof WEEK_DAYS)[number],
  startTime: { hour: number; minute: number },
  endTime: { hour: number; minute: number },
): GeneratedGroupInstanceSlot {
  const startDate = applyTime(date, startTime);
  const endDate = applyTime(date, endTime);
  return { startDate, endDate, day };
}

function firstOccurrenceOnOrAfter(
  startFrom: Date,
  dayIdx: number,
): dayjs.Dayjs {
  const current = dayjs(startFrom).startOf("day");
  const daysToAdd = (dayIdx - current.day() + 7) % 7;
  return current.add(daysToAdd, "day");
}

function addUniqueSlot(
  slots: GeneratedGroupInstanceSlot[],
  seen: Set<string>,
  slot: GeneratedGroupInstanceSlot,
): void {
  const key = groupInstanceSlotKey(slot.startDate);
  if (seen.has(key)) return;
  seen.add(key);
  slots.push(slot);
}

/**
 * Generates recurring group-instance slots for selected weekdays.
 * Mirrors the weekly stepping used in `createRepeatedRequest`, per selected day.
 */
export function generateGroupInstanceSchedule(
  config: GroupInstanceScheduleConfig,
  now: Date = new Date(),
): GeneratedGroupInstanceSlot[] {
  const { days, startFrom, startTime, endTime, rangeMode } = config;
  if (!days.length) return [];

  const slots: GeneratedGroupInstanceSlot[] = [];
  const seen = new Set<string>();
  const nowMs = now.getTime();

  for (const selectedDay of days) {
    const dayIdx = dayIndex(selectedDay);
    if (dayIdx === -1) continue;

    const firstDate = firstOccurrenceOnOrAfter(startFrom, dayIdx);

    if (rangeMode === "weeks") {
      const weekCount = config.weekCount ?? 0;
      if (weekCount < 1) continue;

      for (let i = 0; i < weekCount; i++) {
        const date = firstDate.add(i * 7, "day");
        const slot = buildSlot(date, selectedDay, startTime, endTime);
        if (slot.startDate.getTime() < nowMs) continue;
        addUniqueSlot(slots, seen, slot);
      }
      continue;
    }

    if (!config.endDate) continue;

    const endDay = dayjs(config.endDate).endOf("day");
    let date = firstDate;

    while (!date.isAfter(endDay, "day")) {
      const slot = buildSlot(date, selectedDay, startTime, endTime);
      if (
        slot.startDate.getTime() >= nowMs &&
        !dayjs(slot.startDate).isAfter(endDay)
      ) {
        addUniqueSlot(slots, seen, slot);
      }
      date = date.add(7, "day");
    }
  }

  slots.sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
  return slots;
}

export function buildExistingSlotKeys(
  children: GroupInstanceSlotIdentity[],
): Set<string> {
  const keys = new Set<string>();
  for (const child of children) {
    const key = groupInstanceIdentityKey(child);
    if (!key || key === "|") continue;
    keys.add(key);
  }
  return keys;
}

export function annotateScheduleWithDuplicates(
  slots: GeneratedGroupInstanceSlot[],
  existingKeys: Set<string>,
  location: { location: string; isRemote: boolean },
  now: Date = new Date(),
): GroupInstanceSlotPreview[] {
  const nowMs = now.getTime();
  const resolvedLocation = location.isRemote
    ? REMOTE_LOCATION_LABEL
    : location.location;
  return slots.map((slot) => {
    const key = groupInstanceIdentityKey({
      startDate: slot.startDate,
      hasTime: true,
      location: resolvedLocation,
      isRemote: location.isRemote,
    });
    return {
      ...slot,
      status:
        slot.startDate.getTime() < nowMs
          ? "past"
          : existingKeys.has(key)
            ? "duplicate"
            : "new",
    };
  });
}

/** @alias annotateScheduleWithDuplicates - marks slots as new / duplicate / past */
export function filterDuplicateSlots(
  slots: GeneratedGroupInstanceSlot[],
  existingChildren: GroupInstanceSlotIdentity[],
  location: { location: string; isRemote: boolean },
  now: Date = new Date(),
): GroupInstanceSlotPreview[] {
  return annotateScheduleWithDuplicates(
    slots,
    buildExistingSlotKeys(existingChildren),
    location,
    now,
  );
}

export function validateGroupInstanceScheduleConfig(
  config: GroupInstanceScheduleConfig,
): string | null {
  if (!config.days.length) {
    return "יש לבחור לפחות יום אחד בשבוע";
  }
  if (config.rangeMode === "until_date") {
    if (!config.endDate) return "יש לבחור תאריך סיום";
    if (dayjs(config.endDate).isBefore(dayjs(config.startFrom), "day")) {
      return "תאריך הסיום חייב להיות אחרי תאריך ההתחלה";
    }
  } else if (!config.weekCount || config.weekCount < 1) {
    return "יש להזין לפחות שבוע אחד";
  } else if (config.weekCount > 52) {
    return "מקסימום 52 שבועות";
  }

  const startMins = config.startTime.hour * 60 + config.startTime.minute;
  const endMins = config.endTime.hour * 60 + config.endTime.minute;
  if (endMins <= startMins) {
    return "שעת הסיום חייבת להיות אחרי שעת ההתחלה";
  }

  const schedule = generateGroupInstanceSchedule(config);
  if (schedule.length === 0) {
    return "לא נמצאו מופעים בטווח שנבחר (ייתכן שכולם בעבר)";
  }
  if (schedule.length > GROUP_INSTANCE_BULK_MAX) {
    return `ניתן ליצור עד ${GROUP_INSTANCE_BULK_MAX} מופעים בפעולה אחת - צמצמו את הטווח`;
  }

  return null;
}

export function countSchedulePreview(
  preview: { status: GroupInstanceSlotPreviewStatus }[],
): { toCreate: number; toSkip: number; past: number } {
  let toCreate = 0;
  let toSkip = 0;
  let past = 0;
  for (const row of preview) {
    if (row.status === "new") toCreate++;
    else if (row.status === "duplicate") toSkip++;
    else past++;
  }
  return { toCreate, toSkip, past };
}

export type BulkGroupChildSlotPayload = {
  hasTime: boolean;
  hasStartTime: boolean;
  startDate: Date | null;
  endDate: Date | null;
  timeDescription: string;
  location: string;
  coordinates: { lat: number; lng: number };
  amount: number;
  day: (typeof WEEK_DAYS)[number];
  isRemote: boolean;
};

export function buildBulkGroupChildPreview(
  config: GroupInstanceScheduleConfig,
  existingChildren: GroupInstanceSlotIdentity[],
  location: { location: string; isRemote: boolean },
  now: Date = new Date(),
): GroupInstanceSlotPreview[] {
  const schedule = generateGroupInstanceSchedule(config, now);
  const existingKeys = buildExistingSlotKeys(existingChildren);
  return annotateScheduleWithDuplicates(schedule, existingKeys, location, now);
}

export function buildBulkGroupChildSlots(
  preview: GroupInstanceSlotPreview[],
  options: {
    amount: number;
    location: string;
    coordinates: { lat: number; lng: number };
    isRemote: boolean;
  },
): BulkGroupChildSlotPayload[] {
  return preview
    .filter((row) => row.status === "new")
    .map((row) => ({
      hasTime: true,
      hasStartTime: true,
      startDate: row.startDate,
      endDate: row.endDate,
      timeDescription: "",
      location: options.isRemote ? REMOTE_LOCATION_LABEL : options.location.trim(),
      coordinates: options.isRemote
        ? { lat: 0, lng: 0 }
        : options.coordinates,
      amount: options.amount,
      day: row.day,
      isRemote: options.isRemote,
    }));
}

export function validateBulkMultiLocationConfig(
  locations: BulkLocationEntry[],
  schedule: BulkMultiLocationSchedule,
  now: Date = new Date(),
): string | null {
  if (!locations.length) {
    return "יש להוסיף לפחות מיקום אחד";
  }
  if (locations.length > GROUP_INSTANCE_BULK_MAX) {
    return `ניתן ליצור עד ${GROUP_INSTANCE_BULK_MAX} מופעים בפעולה אחת`;
  }

  for (const entry of locations) {
    if (entry.isRemote) continue;
    if (!entry.location.trim()) {
      return "יש למלא מיקום או לסמן התנדבות מרחוק בכל שורה";
    }
    if (!hasValidCoordinates(entry.coordinates)) {
      return "יש לבחור מיקום מהרשימה או מהמפה בכל שורה";
    }
  }

  if (schedule.hasTime) {
    if (
      !schedule.startDate ||
      !Number.isFinite(new Date(schedule.startDate).getTime())
    ) {
      return "יש לבחור תאריך ושעת התחלה";
    }
    if (
      !schedule.endDate ||
      !Number.isFinite(new Date(schedule.endDate).getTime())
    ) {
      return "יש לבחור שעת סיום";
    }
    if (new Date(schedule.endDate).getTime() <= new Date(schedule.startDate).getTime()) {
      return "שעת הסיום חייבת להיות אחרי שעת ההתחלה";
    }
    if (new Date(schedule.startDate).getTime() < now.getTime()) {
      return "תאריך ההתחלה לא יכול להיות בעבר";
    }
  } else {
    const desc = schedule.timeDescription?.trim() ?? "";
    if (!desc) return "יש למלא תיאור זמן";
    if (desc.length > TIME_DESCRIPTION_MAX_LENGTH) {
      return `תיאור הזמן מוגבל ל-${TIME_DESCRIPTION_MAX_LENGTH} תווים`;
    }
  }

  return null;
}

export function buildBulkMultiLocationPreview(
  locations: BulkLocationEntry[],
  schedule: BulkMultiLocationSchedule,
  existingChildren: GroupInstanceSlotIdentity[],
  now: Date = new Date(),
): MultiLocationSlotPreview[] {
  const existingKeys = buildExistingSlotKeys(existingChildren);
  const batchKeys = new Set<string>();
  const nowMs = now.getTime();

  const startDate = schedule.hasTime ? new Date(schedule.startDate) : null;
  const endDate = schedule.hasTime ? new Date(schedule.endDate) : null;
  const timeDescription = schedule.hasTime
    ? ""
    : schedule.timeDescription.trim();
  const day =
    schedule.hasTime && startDate
      ? WEEK_DAYS[dayjs(startDate).day()]
      : WEEK_DAYS[now.getDay()];

  return locations.map((entry) => {
    const resolvedLocation = entry.isRemote
      ? REMOTE_LOCATION_LABEL
      : entry.location.trim();
    const identity: GroupInstanceSlotIdentity = {
      startDate,
      hasTime: schedule.hasTime,
      timeDescription,
      location: resolvedLocation,
      isRemote: entry.isRemote,
    };
    const key = groupInstanceIdentityKey(identity);

    let status: GroupInstanceSlotPreviewStatus = "new";
    if (
      schedule.hasTime &&
      startDate &&
      startDate.getTime() < nowMs
    ) {
      status = "past";
    } else if (existingKeys.has(key) || batchKeys.has(key)) {
      status = "duplicate";
    } else {
      batchKeys.add(key);
    }

    return {
      location: resolvedLocation,
      coordinates: entry.isRemote ? { lat: 0, lng: 0 } : entry.coordinates,
      isRemote: entry.isRemote,
      hasTime: schedule.hasTime,
      hasStartTime: true,
      startDate,
      endDate,
      timeDescription,
      day,
      status,
    };
  });
}

export function buildBulkMultiLocationSlots(
  preview: MultiLocationSlotPreview[],
  amount: number,
): BulkGroupChildSlotPayload[] {
  return preview
    .filter((row) => row.status === "new")
    .map((row) => ({
      hasTime: row.hasTime,
      hasStartTime: row.hasStartTime,
      startDate: row.startDate,
      endDate: row.endDate,
      timeDescription: row.timeDescription,
      location: row.location,
      coordinates: row.coordinates,
      amount,
      day: row.day,
      isRemote: row.isRemote,
    }));
}
